import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FolderIcon from '@mui/icons-material/Folder';
import ChatIcon from '@mui/icons-material/Chat';
import PeopleIcon from '@mui/icons-material/People';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const Projects = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
  });
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [selectedProjectForMembers, setSelectedProjectForMembers] = useState(null);
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [selectedUserUuid, setSelectedUserUuid] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/api/projects');
      setProjects(response.data.projects || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Ошибка загрузки проектов');
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setFormData({ name: '', description: '' });
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({ name: '', description: '' });
    setError('');
  };

  const handleEditOpen = (project) => {
    setSelectedProject(project);
    setFormData({ 
      name: project.name, 
      description: project.description || '', 
      status: project.status || 'active' 
    });
    setEditDialogOpen(true);
    setError('');
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedProject(null);
    setFormData({ name: '', description: '' });
    setError('');
  };

  const handleDeleteOpen = (project) => {
    setSelectedProject(project);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setSelectedProject(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post('/api/projects', formData);
      setSuccess('Проект успешно создан');
      handleClose();
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка создания проекта');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;
    
    setError('');
    setLoading(true);

    try {
      await axios.put(`/api/projects/${selectedProject.uuid}`, formData);
      setSuccess('Проект успешно обновлен');
      handleEditClose();
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка обновления проекта');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedProject) return;

    try {
      await axios.delete(`/api/projects/${selectedProject.uuid}`);
      setSuccess('Проект успешно удален');
      handleDeleteClose();
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка удаления проекта');
    }
  };

  const handleOpenMembers = async (project) => {
    setSelectedProjectForMembers(project);
    setMembersDialogOpen(true);
    try {
      const response = await axios.get(`/api/projects/${project.uuid}/members`);
      setMembers(response.data.members || []);
    } catch (err) {
      console.error('Error fetching members:', err);
      setError('Ошибка загрузки участников');
    }
  };

  const handleCloseMembers = () => {
    setMembersDialogOpen(false);
    setSelectedProjectForMembers(null);
    setMembers([]);
  };

  const handleOpenAddMember = async () => {
    try {
      const response = await axios.get('/api/users');
      setAllUsers(response.data.users || []);
      setAddMemberDialogOpen(true);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Ошибка загрузки пользователей');
    }
  };

  const handleCloseAddMember = () => {
    setAddMemberDialogOpen(false);
    setSelectedUserUuid('');
  };

  const handleAddMember = async () => {
    if (!selectedUserUuid || !selectedProjectForMembers) return;

    try {
      await axios.post(`/api/projects/${selectedProjectForMembers.uuid}/members`, {
        user_uuid: selectedUserUuid,
      });
      setSuccess('Участник успешно добавлен');
      handleCloseAddMember();
      // Обновляем список участников
      const response = await axios.get(`/api/projects/${selectedProjectForMembers.uuid}/members`);
      setMembers(response.data.members || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка добавления участника');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!selectedProjectForMembers) return;

    try {
      await axios.delete(`/api/projects/${selectedProjectForMembers.uuid}/members/${memberId}`);
      setSuccess('Участник успешно удален');
      // Обновляем список участников
      const response = await axios.get(`/api/projects/${selectedProjectForMembers.uuid}/members`);
      setMembers(response.data.members || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка удаления участника');
    }
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        <Box sx={{ mt: 6, mb: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            Проекты
          </Typography>
          {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'employee') && (
            <Button variant="contained" onClick={handleOpen}>
              Создать проект
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {projects.map((project) => (
            <Grid item xs={12} md={6} key={project.id}>
              <Card sx={{ 
                opacity: project.status !== 'active' ? 0.7 : 1,
                border: project.status !== 'active' ? '1px solid' : 'none',
                borderColor: project.status !== 'active' ? 'error.main' : 'transparent'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Typography variant="h6">{project.name}</Typography>
                    <Box>
                      {(user?.role === 'admin' || user?.role === 'super_admin' || project.created_by_uuid === user?.uuid) && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleEditOpen(project)}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteOpen(project)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Box>
                  {project.description && (
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {project.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip
                      icon={<ChatIcon />}
                      label={`${project.chats_count || 0} чатов`}
                      size="small"
                    />
                    <Chip
                      icon={<FolderIcon />}
                      label={`${project.folders_count || 0} папок`}
                      size="small"
                    />
                  </Box>
                  {project.created_by_username && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Создан: {project.created_by_username}
                    </Typography>
                  )}
                  {(user?.role === 'admin' || user?.role === 'super_admin' || project.created_by_uuid === user?.uuid) && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => navigate(`/projects/${project.uuid}`)}
                      >
                        Управление проектом
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {projects.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Нет созданных проектов
            </Typography>
          </Box>
        )}

        {/* Диалог создания проекта */}
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <form onSubmit={handleSubmit}>
            <DialogTitle>Создать новый проект</DialogTitle>
            <DialogContent>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Название проекта"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Описание проекта"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                margin="normal"
                multiline
                rows={3}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Отмена</Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? 'Создание...' : 'Создать'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Диалог редактирования проекта */}
        <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
          <form onSubmit={handleEdit}>
            <DialogTitle>Редактировать проект</DialogTitle>
            <DialogContent>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Название проекта"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Описание проекта"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                margin="normal"
                multiline
                rows={3}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Статус</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  label="Статус"
                >
                  <MenuItem value="active">Активен</MenuItem>
                  <MenuItem value="inactive">Неактивен</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleEditClose}>Отмена</Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Диалог удаления проекта */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
          <DialogTitle>Удалить проект?</DialogTitle>
          <DialogContent>
            <Typography>
              Вы уверены, что хотите удалить проект "{selectedProject?.name}"? 
              Все чаты и папки в этом проекте будут удалены.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteClose}>Отмена</Button>
            <Button onClick={handleDelete} variant="contained" color="error">
              Удалить
            </Button>
          </DialogActions>
        </Dialog>

        {/* Диалог участников проекта */}
        <Dialog open={membersDialogOpen} onClose={handleCloseMembers} maxWidth="sm" fullWidth>
          <DialogTitle>
            Участники проекта: {selectedProjectForMembers?.name}
            {(user?.role === 'admin' || user?.role === 'super_admin' || selectedProjectForMembers?.created_by_uuid === user?.uuid) && (
              <Button
                variant="contained"
                size="small"
                onClick={handleOpenAddMember}
                sx={{ ml: 2 }}
              >
                Добавить участника
              </Button>
            )}
          </DialogTitle>
          <DialogContent>
            <List>
              {members.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Нет участников
                </Typography>
              ) : (
                members.map((member) => (
                  <ListItem
                    key={member.id}
                    secondaryAction={
                      (user?.role === 'admin' || user?.role === 'super_admin' || selectedProjectForMembers?.created_by_uuid === user?.uuid) && (
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleRemoveMember(member.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )
                    }
                  >
                    <ListItemText
                      primary={member.username}
                      secondary={`Роль: ${member.role === 'member' ? 'Участник' : member.role}`}
                    />
                  </ListItem>
                ))
              )}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseMembers}>Закрыть</Button>
          </DialogActions>
        </Dialog>

        {/* Диалог добавления участника */}
        <Dialog open={addMemberDialogOpen} onClose={handleCloseAddMember} maxWidth="sm" fullWidth>
          <DialogTitle>Добавить участника</DialogTitle>
          <DialogContent>
            <FormControl fullWidth margin="normal">
              <InputLabel>Пользователь</InputLabel>
              <Select
                value={selectedUserUuid}
                onChange={(e) => setSelectedUserUuid(e.target.value)}
                label="Пользователь"
              >
                {allUsers
                  .filter(u => u.uuid !== selectedProjectForMembers?.created_by_uuid && !members.find(m => m.user_uuid === u.uuid))
                  .map((u) => (
                    <MenuItem key={u.uuid} value={u.uuid}>
                      {u.username} ({u.role === 'admin' ? 'Админ' : u.role === 'super_admin' ? 'Супер Админ' : 'Сотрудник'})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddMember}>Отмена</Button>
            <Button onClick={handleAddMember} variant="contained" disabled={!selectedUserUuid}>
              Добавить
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default Projects;
